import { isString, isEmpty, difference, pick, get } from 'lodash';
import { captureException } from 'sentry-error-logging!sofe';

import { nameForCurrentEnv } from './env.helpers.js';
import { cachedToggles } from './cached-toggles.js';
import './opt-in-out/set-up.js';
export useFeatureBubble from './opt-in-out/opt-in-out.hook.js'
export { featureBubble } from './opt-in-out/opt-in-out.js'
export { featureBubbleDefinitions } from './opt-in-out/feature-definitions';

export function fetchFeatureToggles(...allArgs) {
  const toggleNames = allArgs.filter(arg => typeof (arg) === 'string')
  const lastItem = get(allArgs, `[${allArgs.length - 1}]`)
  const config = typeof (lastItem) === 'object' ? lastItem : undefined

  if (toggleNames.length === 0 || toggleNames.find(toggleName => !isString(toggleName) || isEmpty(toggleName))) {
    throw Error(`fetchFeatureToggles must be called with one or more string feature toggle name argument`);
  }

  // This is so Nick can test the feature toggle backend service without deploying it to prod.
  const baseUrl = window.localStorage.getItem('feature-toggle-url') || `https://tm.canopytax.com`;

  const uncachedToggles = difference(toggleNames, Object.keys(cachedToggles));
  const togglesToFetch = uncachedToggles.filter(toggleName => {
    const localStorageValue = window.localStorage.getItem(`feature:${toggleName}`);
    if (localStorageValue) {
      useLocalStorageValue(toggleName, localStorageValue)
      return false;
    } else {
      return true;
    }
  });

  if (togglesToFetch.length > 0) {
    const withMetrics = get(config, 'metrics', true)
    const toggleApiUrl = `${baseUrl}/api/envs/${nameForCurrentEnv()}/toggles?${makeQuery(togglesToFetch, withMetrics)}`
    let responseData
    return fetch(toggleApiUrl)
      .then(response => {
        responseData = response
        if (response.ok) {
          return response.json();
        } else {
          throw new Error(`Feature toggle service responded with status '${response.status}'`);
        }
      })
      .then(newToggleValues => {
        Object.assign(cachedToggles, newToggleValues);
        return filterToggles(toggleNames);
      })
      .catch(e => {
        captureException(e, { response: responseData ? { url: responseData.url, ok: responseData.ok, status: responseData.status, type: responseData.type } : { url: toggleApiUrl } });
        return Promise.reject(e);
      });
  } else {
    return Promise.resolve(filterToggles(toggleNames));
  }
}

export function featureEnabled(...toggleNames) {
  if (toggleNames.length === 0 || toggleNames.find(name => !isString(name) || isEmpty(name))) {
    throw new Error(`featureEnabled must be called with one or more non-empty strings representing the feature name(s)`);
  }

  const unfetchedToggles = toggleNames.filter(toggleName => !cachedToggles.hasOwnProperty(toggleName))
  if (unfetchedToggles.length > 0) {
    throw new Error(`featureEnabled may only be called with previously fetched toggles. '${unfetchedToggles.join(',')}' is not yet fetched.`);
  }

  return toggleNames.some(toggleName => cachedToggles[toggleName]);
}

export function getAllLocalStorageFeatureToggles() {
  const featureKeys = Object.keys(window.localStorage).filter(key => key.includes('feature:'))
  return featureKeys.reduce((accumulator, currentValue) => {
    const newString = currentValue.split('feature:')[1]
    const localStorageValue = localStorage.getItem(currentValue)
    if (localStorageValue === 'on' || localStorageValue === 'true' || localStorageValue === 'off' || localStorageValue === 'false') {
      accumulator[newString] = localStorageValue === 'on' || localStorageValue === 'true'
    }
    return accumulator
  }, {})
}

export function getAllToggleInformationForSentry() {
  return { ...cachedToggles }
}

function filterToggles(togglesToKeep) {
  return pick(cachedToggles, togglesToKeep);
}

function makeQuery(toggles, withMetrics) {
  let featureQuery = `feature=${toggles.join('&feature=')}`
  let query = featureQuery
  let tenantId
  let tenantOverride = localStorage.getItem('tenant-override')
  if (tenantOverride && tenantOverride !== "") {
    tenantId = tenantOverride
  } else if (window.tenant && window.tenant.id) {
    tenantId = window.tenant.id
  }
  if (tenantId) {
    query = `${featureQuery}&enrollment_id=${tenantId}`
  }
  if (withMetrics === false) {
    query = `${query}&metrics=${withMetrics}`
  }
  return query
}

function useLocalStorageValue(toggleName, localStorageValue) {
  const toggleValue = localStorageValue.toLowerCase() === 'on' || localStorageValue.toLowerCase() === 'true';
  /* eslint-disable */
  console.log(`Feature toggle '${toggleName}' is overridden to be '${toggleValue ? 'ON' : 'OFF'}' because of local storage override`);
  /* eslint-enable */
  cachedToggles[toggleName] = toggleValue;
}
